import { motion } from 'framer-motion';
import { Users, Building2, Phone, DollarSign, UserCheck, Crown, Clock, TrendingDown, AlertTriangle } from 'lucide-react';
import { useEffect, useState } from 'react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const BAND_LABELS = {
  under_1m: '< AED 1M', '1m_5m': 'AED 1–5M', '5m_10m': 'AED 5–10M',
  '10m_25m': 'AED 10–25M', '25m_50m': 'AED 25–50M', over_50m: '> AED 50M',
  unknown: 'Unknown'
};

const InventoryGapsCard = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    axios.get(`${API}/admin/inventory-gaps?days=30`)
      .then(r => setData(r.data))
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  }, []);
  return (
    <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.4 }}
      className="glass-card p-6 border border-amber-500/20" data-testid="inventory-gap-card">
      <div className="flex items-start justify-between mb-4">
        <div>
          <div className="flex items-center gap-2 mb-1">
            <TrendingDown className="w-5 h-5 text-amber-400" />
            <h4 className="font-semibold text-lg">Inventory Gap Signals</h4>
          </div>
          <p className="text-text-secondary text-xs">Unmet demand — where users got only aspirational picks (last 30d)</p>
        </div>
        <div className="text-right">
          <div className="text-2xl font-bold text-amber-400" data-testid="inventory-gap-total-signals">{data?.total_signals || 0}</div>
          <div className="text-xs text-text-secondary">signals</div>
        </div>
      </div>
      {loading ? (
        <div className="text-text-secondary text-sm">Loading…</div>
      ) : (data?.gaps || []).length === 0 ? (
        <div className="flex items-center gap-2 text-emerald-400 text-sm py-4">
          <UserCheck className="w-4 h-4" /> No gaps — every onboarded user found exact matches.
        </div>
      ) : (
        <div className="space-y-2">
          <div className="text-xs text-text-secondary uppercase tracking-wider grid grid-cols-12 gap-2 pb-2 border-b border-white/5">
            <div className="col-span-3">Country</div>
            <div className="col-span-4">Budget Band</div>
            <div className="col-span-2 text-right">Users</div>
            <div className="col-span-3 text-right">Signals</div>
          </div>
          {(data.gaps || []).slice(0, 6).map((g, i) => (
            <div key={i} className="grid grid-cols-12 gap-2 items-center py-2 hover:bg-white/5 rounded" data-testid={`inventory-gap-row-${i}`}>
              <div className="col-span-3">
                <span className={`px-2 py-0.5 rounded text-xs ${g.country === 'india' ? 'bg-orange-500/20 text-orange-400' : 'bg-emerald-500/20 text-emerald-400'}`}>
                  {g.country === 'india' ? '🇮🇳 India' : '🇦🇪 UAE'}
                </span>
              </div>
              <div className="col-span-4 text-sm">{BAND_LABELS[g.budget_band] || g.budget_band}</div>
              <div className="col-span-2 text-right text-sm text-text-secondary">{g.unique_users}</div>
              <div className="col-span-3 text-right">
                <span className="inline-flex items-center gap-1 text-amber-400 font-semibold">
                  <AlertTriangle className="w-3 h-3" /> {g.signals}
                </span>
              </div>
            </div>
          ))}
          {data.total_unique_users > 0 && (
            <div className="pt-3 mt-2 border-t border-white/5 text-xs text-text-secondary">
              {data.total_unique_users} unique users affected · prioritize sourcing inventory here
            </div>
          )}
        </div>
      )}
    </motion.div>
  );
};

export const AdminDashboardTab = ({ stats, formatCurrency, setActiveTab }) => (
  <div className="space-y-8">
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="glass-card p-6">
        <Users className="w-8 h-8 text-gold mb-4" />
        <div className="text-3xl font-bold mb-1">{stats?.users?.total || 0}</div>
        <div className="text-text-secondary text-sm">Total Users</div>
        <div className="mt-2 text-xs text-text-secondary">
          Free: {stats?.users?.free || 0} | Premium: {stats?.users?.premium || 0} | Elite: {stats?.users?.elite || 0}
        </div>
      </motion.div>
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }} className="glass-card p-6">
        <Building2 className="w-8 h-8 text-gold mb-4" />
        <div className="text-3xl font-bold mb-1">{stats?.properties || 0}</div>
        <div className="text-text-secondary text-sm">Properties</div>
      </motion.div>
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }} className="glass-card p-6">
        <Phone className="w-8 h-8 text-gold mb-4" />
        <div className="text-3xl font-bold mb-1">{stats?.leads?.total || 0}</div>
        <div className="text-text-secondary text-sm">Total Leads</div>
        <div className="mt-2 text-xs">
          <span className="text-blue-400">New: {stats?.leads?.new || 0}</span> | 
          <span className="text-yellow-400 ml-1">Contacted: {stats?.leads?.contacted || 0}</span> | 
          <span className="text-emerald-400 ml-1">Closed: {stats?.leads?.closed || 0}</span>
        </div>
      </motion.div>
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.3 }} className="glass-card p-6">
        <DollarSign className="w-8 h-8 text-gold mb-4" />
        <div className="text-3xl font-bold mb-1">AED {Number(stats?.revenue?.total || 0).toLocaleString()}</div>
        <div className="text-text-secondary text-sm">Total Revenue</div>
        <div className="mt-2 text-xs text-text-secondary">{stats?.revenue?.transactions || 0} transactions</div>
      </motion.div>
    </div>
    {stats?.pending_partner_applications > 0 && (
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.4 }} className="glass-card p-6 border border-yellow-500/30">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <UserCheck className="w-6 h-6 text-yellow-400" />
            <span className="font-semibold">{stats.pending_partner_applications} Pending Partner Applications</span>
          </div>
          <button onClick={() => setActiveTab('partners')} className="bg-gold text-black px-4 py-2 rounded hover:bg-[#DCC286]">Review</button>
        </div>
      </motion.div>
    )}
    <InventoryGapsCard />
  </div>
);

export const AdminAnalyticsTab = ({ analyticsData }) => (
  <div className="space-y-8">
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="glass-card p-6">
        <Users className="w-8 h-8 text-gold mb-4" />
        <div className="text-3xl font-bold mb-1">{analyticsData?.users?.total || 0}</div>
        <div className="text-text-secondary text-sm">Total Users</div>
        <div className="mt-2 text-xs text-emerald-400">+{analyticsData?.users?.new_7d || 0} this week ({analyticsData?.users?.growth_rate || 0}% growth)</div>
      </motion.div>
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }} className="glass-card p-6">
        <DollarSign className="w-8 h-8 text-gold mb-4" />
        <div className="text-3xl font-bold mb-1">AED {Number(analyticsData?.revenue?.total || 0).toLocaleString()}</div>
        <div className="text-text-secondary text-sm">Total Revenue</div>
        <div className="mt-2 text-xs text-emerald-400">+AED {Number(analyticsData?.revenue?.last_7d || 0).toLocaleString()} this week</div>
      </motion.div>
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }} className="glass-card p-6">
        <Phone className="w-8 h-8 text-gold mb-4" />
        <div className="text-3xl font-bold mb-1">{analyticsData?.engagement?.leads_total || 0}</div>
        <div className="text-text-secondary text-sm">Total Leads</div>
        <div className="mt-2 text-xs text-emerald-400">+{analyticsData?.engagement?.leads_7d || 0} this week</div>
      </motion.div>
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.3 }} className="glass-card p-6">
        <Crown className="w-8 h-8 text-gold mb-4" />
        <div className="text-3xl font-bold mb-1">{analyticsData?.subscriptions?.conversion_rate || 0}%</div>
        <div className="text-text-secondary text-sm">Conversion Rate</div>
        <div className="mt-2 text-xs">Premium: {analyticsData?.subscriptions?.premium || 0} | Elite: {analyticsData?.subscriptions?.elite || 0}</div>
      </motion.div>
    </div>
    <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.4 }} className="glass-card p-6">
        <Building2 className="w-6 h-6 text-gold mb-3" />
        <div className="text-2xl font-bold mb-1">{analyticsData?.engagement?.properties || 0}</div>
        <div className="text-text-secondary text-sm">Properties Listed</div>
      </motion.div>
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.5 }} className="glass-card p-6">
        <Clock className="w-6 h-6 text-gold mb-3" />
        <div className="text-2xl font-bold mb-1">{analyticsData?.engagement?.viewings_7d || 0}</div>
        <div className="text-text-secondary text-sm">Viewings This Week</div>
      </motion.div>
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.6 }} className="glass-card p-6">
        <UserCheck className="w-6 h-6 text-gold mb-3" />
        <div className="text-2xl font-bold mb-1">{analyticsData?.partners?.approved || 0}</div>
        <div className="text-text-secondary text-sm">Active Partners</div>
        <div className="mt-2 text-xs text-text-secondary">Total registered: {analyticsData?.partners?.total || 0}</div>
      </motion.div>
    </div>
    <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.7 }} className="glass-card p-6">
      <h3 className="font-semibold text-lg mb-4">Daily Signups (Last 30 Days)</h3>
      <div className="flex items-end gap-1 h-40">
        {analyticsData?.charts?.daily_signups?.slice(-30).map((day, idx) => (
          <div key={day.date || `day-${idx}`} className="flex-1 flex flex-col items-center">
            <div className="w-full bg-gold/80 rounded-t transition-all hover:bg-gold" style={{ height: `${Math.max(day.count * 20, 4)}px` }} title={`${day.date}: ${day.count} signups`} />
          </div>
        ))}
      </div>
      <div className="flex justify-between mt-2 text-xs text-text-secondary">
        <span>30 days ago</span><span>Today</span>
      </div>
    </motion.div>
  </div>
);
